import { useInjection } from 'inversify-react';
import { capitalize } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import useDraggableScroll from 'use-draggable-scroll';
import { ANNOUNCEMENTS_TYPE, SORT_ON } from '../../../constants/ANNOUNCEMENT';
import MESSAGE_TYPES from '../../../constants/MESSAGE_TYPES';
import { prepareSelectedFilter } from '../../../helpers/announcement';
import { formatDateAnnouncement } from '../../../helpers/dates';
import useAnnouncements from '../../../hooks/useAnnouncements';
import useTranslate from '../../../hooks/useTranslate';
import { IMetaController } from '../../../lib/widget/meta/meta.interface';
import { META_MODULE_IDS } from '../../../lib/widget/meta/meta.module';
import { TSideBarPosition } from '../../../types/sideBar';
import Announcements from '../Announcements';
import Gripper from '../Gripper';
import { SubFilters } from './filters/SubFilters';
import { SidebarTypeFilter } from './SidebarTypeFilter';
import { SortAnnouncement } from './sort';
import Styled from './styled.landing';

type TSidebar = FC<{
  isGripperDisabled: boolean;
  setPosition: (newPosition: TSideBarPosition) => void;
  position: TSideBarPosition;
}>;

const SidebarLanding: TSidebar = ({ isGripperDisabled }) => {
  const { t, i18n } = useTranslate();
  const {
    eventData: {
      title: eventTitle,
      place: {
        title: placeTitle,
        location: { title: locationTitle },
      },
    },
  } = window.Widget.settings;
  const {
    announcementsController: { announcements, count: announcementsCount, announcementsGroup },
  } = window.Widget;
  const {
    standingTicketsController: { count: standingTicketsCount },
  } = window.Widget;

  const metaController: IMetaController = useInjection(META_MODULE_IDS.META_CONTROLLER);

  const count = announcementsCount + standingTicketsCount;

  const { setFilter, selectedFilter } = useAnnouncements();
  const typeFilterRef = useRef(null);
  const { onMouseDown: onTypeFilterMouseDown } = useDraggableScroll(typeFilterRef);
  const [selectedSubFilter, setSubSelectedFilter] = useState<string | null>(
    selectedFilter !== ANNOUNCEMENTS_TYPE.ALL && ANNOUNCEMENTS_TYPE.ALL,
  );
  const [sortOn, setSortOn] = useState(SORT_ON.ASC);
  const [isMouseDown, setIsMouseDown] = useState(false);

  const getAnnouncementCount = () => {
    if (!selectedFilter) {
      return null;
    }
    if (selectedFilter === ANNOUNCEMENTS_TYPE.ALL) {
      return count;
    }
    if (selectedFilter && selectedSubFilter !== ANNOUNCEMENTS_TYPE.ALL) {
      return announcementsGroup[selectedFilter][selectedSubFilter].length;
    } else {
      return announcements[selectedFilter.toLocaleUpperCase()].length;
    }
  };

  // const {
  //   // prices: { max: maxPrice, min: minPrice },
  //   // activeItem,
  // } = announcements;

  const handleMouseUp = (event) => {
    if (isMouseDown) {
      setIsMouseDown(false);
      event.stopPropagation();
    }
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMouseDown]);

  const scrollFilter = (event) => {
    onTypeFilterMouseDown(event);
    setIsMouseDown(true);
  };

  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    const selectedDateChange = (date: string) => {
      setSelectedDate(date);
    };

    metaController.on(MESSAGE_TYPES.GET_SELECTED_DATE, selectedDateChange);

    return () => {
      metaController.off(MESSAGE_TYPES.GET_SELECTED_DATE, selectedDateChange);
    };
  }, []);

  if (count === 0) {
    return null;
  }

  return (
    <Styled.SideBar>
      <Styled.Header>
        <Gripper isDisabled={isGripperDisabled} />
        <div>
          <Styled.EventTitle>{eventTitle}</Styled.EventTitle>
          <Styled.EventInfo isEmpty={!selectedDate}>
            <span>{formatDateAnnouncement(selectedDate, i18n.language)}</span> ·
            <span>
              {capitalize(placeTitle)}, {t('city')} {locationTitle}
            </span>
          </Styled.EventInfo>
        </div>

        <Styled.FlexHelper>
          <div>
            <Styled.Prices>
              {/* {minPrice === maxPrice
                ? `Цена ${minPrice} ₽`
                : `Билеты от ${minPrice} - ${maxPrice} ₽`} */}
            </Styled.Prices>
          </div>
          {/* <Styled.FiltersOpenBtn onClick={openFilters} /> */}
        </Styled.FlexHelper>

        <Styled.TypeFilter ref={typeFilterRef} onMouseDown={scrollFilter}>
          <li>
            <SidebarTypeFilter
              type={ANNOUNCEMENTS_TYPE.ALL}
              onSelect={setFilter}
              selectedFilter={selectedFilter}
            >
              {t('announcements_filter_all')}
            </SidebarTypeFilter>
          </li>
          {prepareSelectedFilter(announcements).map((key) => {
            const type = key.toLowerCase();

            return (
              <li key={key}>
                <SidebarTypeFilter
                  type={key.toLowerCase()}
                  onSelect={setFilter}
                  setSubSelectedFilter={setSubSelectedFilter}
                  selectedFilter={selectedFilter}
                >
                  {type === ANNOUNCEMENTS_TYPE.ROW && t('announcements_filter_row')}
                  {type === ANNOUNCEMENTS_TYPE.CATEGORY && t('announcements_filter_category')}
                  {type === ANNOUNCEMENTS_TYPE.SECTOR && t('announcements_filter_sector')}
                </SidebarTypeFilter>
              </li>
            );
          })}
        </Styled.TypeFilter>

        {selectedFilter !== ANNOUNCEMENTS_TYPE.ALL && announcementsGroup[selectedFilter] && (
          <SubFilters
            t={t}
            announcementsGroup={announcementsGroup}
            filterType={selectedFilter}
            setSubSelectedFilter={setSubSelectedFilter}
            selectedSubFilter={selectedSubFilter}
            setIsMouseDown={setIsMouseDown}
          />
        )}

        <SortAnnouncement
          announcementCount={getAnnouncementCount()}
          sortOn={sortOn}
          setSortOn={setSortOn}
        />
      </Styled.Header>

      {count === 0 && <Styled.Empty>{t('empty_announcements')}</Styled.Empty>}
      <Announcements
        selectedFilter={selectedFilter}
        selectedSubFilter={selectedSubFilter}
        sortOn={sortOn}
        announcements={announcements}
      />
    </Styled.SideBar>
  );
};

export default SidebarLanding;
